import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { css, html } from '../../lib/lit.js';
import { router } from '../../lib/router.js';

class AlertChannelsView extends BaseComponent {
    static properties = {
        _channels: { type: Array, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _view: { type: String, state: true },
        _formData: { type: Object, state: true },
        _saving: { type: Boolean, state: true },
        _editId: { type: String, state: true },
        _confirmDelete: { type: String, state: true },
        _confirmRegenerate: { type: String, state: true },
    };

    static styles = css`
        :host { display: block; }
        .secret-cell {
            font-family: monospace;
            font-size: 0.8rem;
        }
        .secret-full {
            font-family: monospace;
            font-size: 0.85rem;
            word-break: break-all;
            background: #f8f9fa;
            padding: 0.5rem;
            border-radius: 0.25rem;
            border: 1px solid #dee2e6;
        }
    `;

    constructor() {
        super();
        this._channels = [];
        this._loading = true;
        this._error = null;
        this._view = 'list';
        this._formData = {
            service: '',
            environment: 'prd',
            discord_channel_id: '',
        };
        this._saving = false;
        this._editId = null;
        this._confirmDelete = null;
        this._confirmRegenerate = null;
    }

    async connectedCallback() {
        super.connectedCallback();
        this._unsubscribeRouter = router.subscribe((state) => {
            if (state.path === '/alert-channels/new') {
                this._view = 'create';
                this._formData = {
                    service: '',
                    environment: 'prd',
                    discord_channel_id: '',
                };
                this._editId = null;
            } else if (state.params?.id) {
                this._startEdit(state.params.id);
            } else {
                this._view = 'list';
            }
        });
        this._loadChannelsPromise = this._loadChannels();
        const path = window.location.pathname;
        if (path === '/alert-channels/new') {
            this._view = 'create';
        } else if (path.match(/\/alert-channels\/[^/]+\/edit/)) {
            const id = path.split('/')[2];
            this._startEdit(id);
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._unsubscribeRouter) this._unsubscribeRouter();
    }

    async _loadChannels() {
        this._loading = true;
        this._error = null;
        try {
            this._channels = await api.get('/api/alert-channels');
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    async _startEdit(id) {
        await this._loadChannelsPromise;
        const channel = this._channels.find((c) => c.ID === id);
        if (channel) {
            this._formData = {
                service: channel.Service,
                environment: channel.Environment,
                discord_channel_id: channel.DiscordChannelID,
            };
            this._editId = id;
            this._view = 'edit';
        }
    }

    async _submitCreate(e) {
        e.preventDefault();
        this._saving = true;
        this._error = null;
        try {
            const created = await api.post('/api/alert-channels', this._formData);
            await this._loadChannels();
            // Show the secret to the user after creation
            this._newlyCreatedSecret = created.WebhookSecret;
            this._newlyCreatedService = created.Service;
            this._view = 'created';
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    async _submitEdit(e) {
        e.preventDefault();
        this._saving = true;
        this._error = null;
        try {
            await api.put(`/api/alert-channels/${this._editId}`, this._formData);
            await this._loadChannels();
            router.navigate('/alert-channels');
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    async _regenerateSecret(id) {
        this._error = null;
        try {
            const updated = await api.post(`/api/alert-channels/${id}/regenerate-secret`);
            this._confirmRegenerate = null;
            this._newlyCreatedSecret = updated.WebhookSecret;
            this._newlyCreatedService = updated.Service;
            this._view = 'created';
            await this._loadChannels();
        } catch (err) {
            this._error = err.message;
        }
    }

    async _testChannel(id) {
        this._error = null;
        try {
            await api.post(`/api/alert-channels/${id}/test`);
        } catch (err) {
            this._error = err.message;
        }
    }

    async _deleteChannel(id) {
        this._error = null;
        try {
            await api.delete(`/api/alert-channels/${id}`);
            this._confirmDelete = null;
            await this._loadChannels();
        } catch (err) {
            this._error = err.message;
        }
    }

    _updateField(field, value) {
        this._formData = { ...this._formData, [field]: value };
    }

    _copySecret() {
        navigator.clipboard.writeText(this._newlyCreatedSecret);
    }

    render() {
        if (this._view === 'created') return this._renderSecretDisplay();
        if (this._view === 'create')
            return this._renderForm('Create Alert Channel', (e) => this._submitCreate(e));
        if (this._view === 'edit')
            return this._renderForm('Edit Alert Channel', (e) => this._submitEdit(e));
        return this._renderList();
    }

    _renderSecretDisplay() {
        return html`
            <div class="d-flex align-items-center gap-2 mb-3">
                <h5 class="mb-0">Webhook Secret</h5>
            </div>

            <div class="alert alert-warning">
                <i class="bi bi-exclamation-triangle"></i>
                Copy this secret now. It won't be shown in full again.
            </div>

            <div class="mb-3">
                <label class="form-label fw-medium">Secret for <code>${this._newlyCreatedService}</code></label>
                <div class="secret-full">${this._newlyCreatedSecret}</div>
            </div>

            <p class="text-muted" style="font-size: 0.875rem;">
                Use this as the Bearer token the alert sender (d3-core-infra) presents to
                <code>/api/webhooks/alert</code>.
            </p>

            <div class="d-flex gap-2">
                <button class="btn btn-outline-primary btn-sm" @click="${this._copySecret}">
                    <i class="bi bi-clipboard"></i> Copy
                </button>
                <a href="/alert-channels" class="btn btn-primary btn-sm">Done</a>
            </div>
        `;
    }

    _renderList() {
        return html`
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0">Alert Channels</h5>
                <a href="/alert-channels/new" class="btn btn-primary btn-sm">
                    <i class="bi bi-plus-lg"></i> Add Channel
                </a>
            </div>

            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}

            ${
                this._loading
                    ? html`
                <div class="text-center py-4">
                    <div class="spinner-border spinner-border-sm text-primary"></div>
                </div>
            `
                    : html`
                ${
                    this._channels.length === 0
                        ? html`
                    <div class="text-muted text-center py-4">No alert channels configured.</div>
                `
                        : html`
                    <table class="table table-hover">
                        <thead>
                            <tr>
                                <th>Service</th>
                                <th>Environment</th>
                                <th>Channel ID</th>
                                <th>Secret</th>
                                <th style="width: 180px;"></th>
                            </tr>
                        </thead>
                        <tbody>
                            ${this._channels.map(
                                (ch) => html`
                                <tr>
                                    <td><code>${ch.Service}</code></td>
                                    <td><span class="badge bg-secondary">${ch.Environment}</span></td>
                                    <td><code>${ch.DiscordChannelID}</code></td>
                                    <td class="secret-cell">${ch.WebhookSecret.substring(0, 8)}...</td>
                                    <td class="text-end">
                                        <button class="btn btn-outline-info btn-sm me-1" title="Send test alert" @click="${() => this._testChannel(ch.ID)}">
                                            <i class="bi bi-send"></i>
                                        </button>
                                        ${
                                            this._confirmRegenerate === ch.ID
                                                ? html`
                                            <button class="btn btn-warning btn-sm me-1" @click="${() => this._regenerateSecret(ch.ID)}">
                                                Confirm
                                            </button>
                                        `
                                                : html`
                                            <button class="btn btn-outline-warning btn-sm me-1" title="Regenerate secret" @click="${() => (this._confirmRegenerate = ch.ID)}">
                                                <i class="bi bi-key"></i>
                                            </button>
                                        `
                                        }
                                        <a href="/alert-channels/${ch.ID}/edit" class="btn btn-outline-secondary btn-sm me-1">
                                            <i class="bi bi-pencil"></i>
                                        </a>
                                        ${
                                            this._confirmDelete === ch.ID
                                                ? html`
                                            <button class="btn btn-danger btn-sm" @click="${() => this._deleteChannel(ch.ID)}">
                                                Confirm
                                            </button>
                                        `
                                                : html`
                                            <button class="btn btn-outline-danger btn-sm" @click="${() => (this._confirmDelete = ch.ID)}">
                                                <i class="bi bi-trash"></i>
                                            </button>
                                        `
                                        }
                                    </td>
                                </tr>
                            `,
                            )}
                        </tbody>
                    </table>
                `
                }
            `
            }
        `;
    }

    _renderForm(title, onSubmit) {
        return html`
            <div class="d-flex align-items-center gap-2 mb-3">
                <a href="/alert-channels" class="btn btn-outline-secondary btn-sm">
                    <i class="bi bi-arrow-left"></i>
                </a>
                <h5 class="mb-0">${title}</h5>
            </div>

            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}

            <form @submit="${onSubmit}" style="max-width: 500px;">
                <div class="mb-3">
                    <label class="form-label">Service</label>
                    <input type="text" class="form-control" placeholder="e.g. d3-vtt"
                           .value="${this._formData.service}"
                           @input="${(e) => this._updateField('service', e.target.value)}"
                           required>
                    <div class="form-text">The service whose alerts route here (matches the alert payload's <code>service</code>)</div>
                </div>

                <div class="mb-3">
                    <label class="form-label">Environment</label>
                    <select class="form-select"
                            .value="${this._formData.environment}"
                            @change="${(e) => this._updateField('environment', e.target.value)}">
                        <option value="prd">prd</option>
                        <option value="dev">dev</option>
                    </select>
                </div>

                <div class="mb-3">
                    <label class="form-label">Discord Channel ID</label>
                    <input type="text" class="form-control" placeholder="e.g. 1234567890"
                           .value="${this._formData.discord_channel_id}"
                           @input="${(e) => this._updateField('discord_channel_id', e.target.value)}"
                           required>
                    <div class="form-text">Enable Developer Mode in Discord settings, then right-click the channel and Copy Channel ID</div>
                </div>

                <div class="d-flex gap-2">
                    <button type="submit" class="btn btn-primary" ?disabled="${this._saving}">
                        ${this._saving ? html`<span class="spinner-border spinner-border-sm"></span>` : 'Save'}
                    </button>
                    <a href="/alert-channels" class="btn btn-outline-secondary">Cancel</a>
                </div>
            </form>
        `;
    }
}

customElements.define('alert-channels-view', AlertChannelsView);
